import { useMemo, useState } from 'react';
import {
  Button,
  Badge,
  Select,
  Input,
  Modal,
  LoadingSkeleton,
} from '@nexora/design-system';
import { useToast } from '@nexora/design-system';
import {
  useReservations,
  useCreateReservation,
  useReleaseReservation,
  useWarehouses,
  type ReservationStatus,
} from './useInventory';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';

const STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Ativa',
  CONSUMED: 'Consumida',
  CANCELLED: 'Cancelada',
  EXPIRED: 'Expirada',
};

const STATUS_VARIANTS: Record<string, 'success' | 'info' | 'warning' | 'error' | 'neutral'> = {
  ACTIVE: 'success',
  CONSUMED: 'info',
  CANCELLED: 'error',
  EXPIRED: 'warning',
};

const ORIGIN_OPTIONS = [
  { value: 'SALES_ORDER', label: 'Pedido de Venda' },
  { value: 'PURCHASE_ORDER', label: 'Ordem de Compra' },
  { value: 'PRODUCTION_ORDER', label: 'Ordem de Produção' },
  { value: 'MANUAL', label: 'Manual' },
];

function statusBadge(status: ReservationStatus): JSX.Element {
  return (
    <Badge variant={STATUS_VARIANTS[status] ?? 'neutral'} size="sm">
      {STATUS_LABELS[status] ?? status}
    </Badge>
  );
}

export function ReservationsPage(): JSX.Element {
  const { addToast } = useToast();

  const [page, setPage] = useState(0);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterWarehouse, setFilterWarehouse] = useState('');
  const [filterProduct, setFilterProduct] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmRelease, setConfirmRelease] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    originType: 'MANUAL',
    originId: '',
    productId: '',
    quantity: '',
    warehouseId: '',
    expiresAt: '',
  });

  const { data: warehouses } = useWarehouses();
  const { data: products } = useQuery({
    queryKey: ['products', 'nomes'],
    queryFn: async () => apiClient.get<{ id: string; name: string }[]>('/products'),
    staleTime: 5 * 60 * 1000,
  });
  const warehouseName = useMemo(() => {
    const map = new Map<string, string>();
    for (const w of warehouses ?? []) map.set(w.id, `${w.code} — ${w.name}`);
    return map;
  }, [warehouses]);
  const productName = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of products ?? []) map.set(p.id, p.name);
    return map;
  }, [products]);

  const limit = 20;

  const params: { page: number; limit: number; status?: string; warehouseId?: string; productId?: string } = { page, limit };
  if (filterStatus !== 'all') params.status = filterStatus;
  if (filterWarehouse) params.warehouseId = filterWarehouse;
  if (filterProduct) params.productId = filterProduct;

  const { data, isLoading, error, refetch } = useReservations(params);
  const createReservation = useCreateReservation();
  const releaseReservation = useReleaseReservation();

  const reservations = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  const handleCreate = async () => {
    if (!formData.productId || !formData.quantity.trim() || !formData.originId.trim()) {
      addToast('Preencha todos os campos obrigatórios', 'error');
      return;
    }
    if (!formData.warehouseId) {
      addToast('Escolha o armazém', 'error');
      return;
    }
    const quantity = Number(formData.quantity);
    if (Number.isNaN(quantity) || quantity <= 0) {
      addToast('Quantidade inválida', 'error');
      return;
    }

      try {
      await createReservation.mutateAsync({
        warehouseId: formData.warehouseId,
        productId: formData.productId,
        quantity,
        originType: formData.originType,
        originId: formData.originId,
        expiresAt: formData.expiresAt || undefined,
      });
      addToast('Reserva criada com sucesso', 'success');
      setModalOpen(false);
      setFormData({ originType: 'MANUAL', originId: '', productId: '', quantity: '', warehouseId: '', expiresAt: '' });
    } catch {
      addToast('Erro ao criar reserva', 'error');
    }
  };

  const handleRelease = async (id: string) => {
    try {
      await releaseReservation.mutateAsync(id);
      addToast('Reserva liberada com sucesso', 'success');
      setConfirmRelease(null);
    } catch {
      addToast('Erro ao liberar reserva', 'error');
    }
  };

  return (
    <div className="flex-1 overflow-auto bg-surface-bright p-md md:p-lg">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Reservas de Estoque</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Reservas de estoque por pedido.</p>
        </div>
        <div className="flex flex-wrap gap-sm">
          <Button onClick={() => setModalOpen(true)}>+ Nova Reserva</Button>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="reserv-filters bg-surface-container-lowest rounded-xl border border-outline-variant shadow-sm p-md">
        <Select
          label="Status"
          value={filterStatus}
          onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}
        >
          <option value="all">Todos</option>
          <option value="ACTIVE">Ativa</option>
          <option value="CONSUMED">Consumida</option>
          <option value="CANCELLED">Cancelada</option>
          <option value="EXPIRED">Expirada</option>
        </Select>
        <Select
          label="Armazém"
          value={filterWarehouse}
          onChange={(e) => { setFilterWarehouse(e.target.value); setPage(0); }}
        >
          <option value="">Todos</option>
          {(warehouses ?? []).map((w) => (
            <option key={w.id} value={w.id}>
              {w.code} — {w.name}
            </option>
          ))}
        </Select>
        <Select
          label="Produto"
          value={filterProduct}
          onChange={(e) => { setFilterProduct(e.target.value); setPage(0); }}
        >
          <option value="">Todos</option>
          {(products ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </Select>
      </div>

      <div className="bg-surface-container-lowest rounded-xl border border-outline-variant shadow-sm overflow-hidden">
        <div className="p-md border-b border-outline-variant flex items-center justify-between gap-md">
          <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Reservas</h3>
          <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
            {total} registro{total !== 1 ? 's' : ''}
          </span>
        </div>

        {/* ── Loading ── */}
        {isLoading && (
          <div style={{ padding: '1rem' }}>
            <LoadingSkeleton count={6} height="48px" />
          </div>
        )}

        {/* ── Error ── */}
        {error && !isLoading && (
          <div className="state-error" role="alert">
            <p>Erro ao carregar reservas</p>
            <p className="state-detail">{(error as Error).message}</p>
            <Button size="sm" variant="outline" onClick={() => refetch()}>Tentar novamente</Button>
          </div>
        )}

        {/* ── Empty ── */}
        {!isLoading && !error && reservations.length === 0 && (
          <div className="state-empty">
            <p>Nenhuma reserva encontrada</p>
            <p className="state-detail">
              {filterStatus !== 'all' || filterWarehouse || filterProduct
                ? 'Nenhuma reserva corresponde aos filtros aplicados.'
                : 'Crie a primeira reserva para começar.'}
            </p>
          </div>
        )}

        {/* ── Data ── */}
        {!isLoading && !error && reservations.length > 0 && (
          <>
            <div className="reserv-table-wrap">
              <table className="reserv-table">
                <thead>
                  <tr>
                    <th>Produto</th>
                    <th>Armazém</th>
                    <th>Quantidade</th>
                    <th>Status</th>
                    <th>Origem</th>
                    <th>ID Origem</th>
                    <th>Expira em</th>
                    <th>Criado em</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {reservations.map((r) => (
                    <tr key={r.id}>
                      <td>{productName.get(r.productId) ?? r.productId.slice(0, 8)}</td>
                      <td>{warehouseName.get(r.warehouseId) ?? r.warehouseId.slice(0, 8)}</td>
                      <td style={{ fontWeight: 600 }}>{r.quantity}</td>
                      <td>{statusBadge(r.status)}</td>
                      <td>{r.originType}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.8125rem', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {r.originId}
                      </td>
                      <td>{r.expiresAt ? new Date(r.expiresAt).toLocaleDateString('pt-BR') : '-'}</td>
                      <td>{new Date(r.createdAt).toLocaleDateString('pt-BR')}</td>
                      <td>
                        {r.status === 'ACTIVE' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            style={{ color: '#ba1a1a' }}
                            onClick={() => setConfirmRelease(r.id)}
                            loading={releaseReservation.isPending && confirmRelease === r.id}
                          >
                            Liberar
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ── Pagination ── */}
            {totalPages > 1 && (
              <div className="reserv-pagination">
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={page <= 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Anterior
                </Button>
                <span className="reserv-page-info">
                  Página {page + 1} de {totalPages} ({total} registros)
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Próxima
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Confirm Release Modal ── */}
      <Modal
        isOpen={!!confirmRelease}
        onClose={() => setConfirmRelease(null)}
        title="Liberar Reserva"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmRelease(null)}>Cancelar</Button>
            <Button
              variant="primary"
              style={{ background: '#ba1a1a', borderColor: '#ba1a1a' }}
              onClick={() => confirmRelease && handleRelease(confirmRelease)}
              loading={releaseReservation.isPending}
            >
              Confirmar Liberação
            </Button>
          </>
        }
      >
        <p style={{ fontSize: '0.875rem', color: '#43474e', margin: 0 }}>
          Tem certeza que deseja liberar esta reserva? O estoque disponível será atualizado.
        </p>
      </Modal>

      {/* ── Create Reservation Modal ── */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Nova Reserva"
        footer={
          <>
            <Button variant="ghost" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreate} loading={createReservation.isPending}>Criar Reserva</Button>
          </>
        }
      >
        <div className="reserv-form">
          <Select
            label="Produto *"
            value={formData.productId}
            onChange={(e) => setFormData((prev) => ({ ...prev, productId: e.target.value }))}
          >
            <option value="">Selecione…</option>
            {(products ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
          <Input
            label="Quantidade *"
            type="number"
            placeholder="Ex: 10"
            value={formData.quantity}
            onChange={(e) => setFormData((prev) => ({ ...prev, quantity: e.target.value }))}
          />
          <Select
            label="Armazém *"
            value={formData.warehouseId}
            onChange={(e) => setFormData((prev) => ({ ...prev, warehouseId: e.target.value }))}
          >
            <option value="">Selecione…</option>
            {(warehouses ?? []).map((w) => (
              <option key={w.id} value={w.id}>
                {w.code} — {w.name}
              </option>
            ))}
          </Select>
          <Select
            label="Tipo de Origem *"
            value={formData.originType}
            onChange={(e) => setFormData((prev) => ({ ...prev, originType: e.target.value }))}
          >
            {ORIGIN_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Select>
          <Input
            label="ID da Origem *"
            placeholder="UUID do pedido/OC/OP"
            value={formData.originId}
            onChange={(e) => setFormData((prev) => ({ ...prev, originId: e.target.value }))}
          />
          <Input
            label="Data de Expiração (opcional)"
            type="date"
            value={formData.expiresAt}
            onChange={(e) => setFormData((prev) => ({ ...prev, expiresAt: e.target.value }))}
          />
        </div>
      </Modal>

      <style>{`
        .reserv-filters { display: flex; align-items: flex-end; gap: 1rem; margin-bottom: 1.5rem; flex-wrap: wrap; }
        .reserv-filters > * { min-width: 160px; flex: 1; max-width: 220px; }
        .reserv-table-wrap { overflow-x: auto; }
        .reserv-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .reserv-table th { padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e; font-weight: 600; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid #e0e3e5; text-align: left; white-space: nowrap; position: sticky; top: 0; z-index: 1;}
        .reserv-table td { padding: 0.75rem 1rem; border-bottom: 1px solid #eceef0; color: #191c1e; }
        .reserv-table tbody tr:hover td { background: #eceef0; }
        .reserv-pagination { display: flex; align-items: center; justify-content: center; gap: 1rem; padding: 1rem; }
        .reserv-page-info { font-size: 0.8125rem; color: #43474e; }
        .reserv-form { display: flex; flex-direction: column; gap: 1rem; }
        .state-error { text-align: center; padding: 2rem; }
        .state-error p { margin: 0; color: #ba1a1a; font-weight: 600; }
        .state-error .state-detail { color: #74777f; font-size: 0.875rem; font-weight: 400; margin-top: 0.5rem; }
        .state-empty { text-align: center; padding: 2rem; color: #74777f; }
        .state-empty .state-detail { font-size: 0.875rem; margin-top: 0.5rem; }
      `}</style>
    </div>
  );
}
